import React, { useEffect, useRef } from 'react';
import { AnyAction, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useAppDispatch } from '@wfp-root-app/store/store';
import { ThunkDispatch } from 'redux-thunk';
import State from '@wfp-root-app/store/state';
import { routerHelperActions } from '@wfp-common/store/routerHelperSlice';
import { useNavigate } from '@wfp-common/hooks/useNavigate';
import styled from 'styled-components';
import { useSearchParams as useRouterSearchParams } from 'react-router-dom';

import { ActionCreators, ActionTypes } from './auth';
import { AppConfigActionCreators } from '../app/appConfig';
import { Loader } from '../utils/Loader';
import { authLogout, authVerifyLogin } from '../apiClient';
import { windowStorage, WindowStorageToken } from '../windowStorage';
import { resetSessionProlongAlertState } from '../utils/sessionExpiration/sessionExpiration';
import Manager from '../managers/manager';

interface LoginResponse {
    id_token: string;
    access_token: string;
    user: Manager;
    return_url?: string;
}

interface VerifyLoginPageProps {
    logout?: () => void;
}

const VerifyLoginPage: React.FC<VerifyLoginPageProps> = () => {
    const [pending, setPending] = React.useState(false);
    const attemptedRef = useRef(false);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useRouterSearchParams();

    useEffect(() => {
        const state = searchParams.get('state');
        const code = searchParams.get('code');

        // Prevent duplicate calls and ensure we have required params
        if (pending || attemptedRef.current || !state || !code) {
            return;
        }

        attemptedRef.current = true;
        setPending(true);

        // Strip code/state from URL to prevent reuse if the component remounts
        window.history.replaceState({}, '', window.location.pathname);

        authVerifyLogin(state, code)
            .then((res: LoginResponse) => {
                setPending(false);
                windowStorage.setItem(WindowStorageToken.managerApiToken, res.id_token);
                dispatch({
                    type: ActionTypes.loginSuccessful,
                    payload: {
                        apiToken: res.access_token,
                        manager: res.user,
                    },
                });
                dispatch(resetSessionProlongAlertState);
                dispatch(AppConfigActionCreators.loadApplicationConfig());
                // Authentik's verify endpoint returns a user without full profile.permissions,
                // so re-fetch the full manager (same call initApp runs on page refresh).
                // Only then refresh pending actions — otherwise badge counts read stale permissions.
                dispatch(ActionCreators.reloadUserData());

                const returnUrl = sessionStorage.getItem('returnUrl') || res.return_url;
                if (returnUrl) {
                    sessionStorage.removeItem('returnUrl');
                    dispatch(routerHelperActions.makeRedirect(returnUrl));
                } else {
                    dispatch(routerHelperActions.makeRedirect('/home'));
                }
            })
            .catch(async () => {
                try {
                    await authLogout();
                } catch {
                    // ignore logout errors
                }
                navigate('/forbidden');
            });
    }, [searchParams]);

    return (
        <LoaderContainer>
            <Loader />
        </LoaderContainer>
    );
};

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    font-size: 22px;
`;

function mapStateToProps(state: State) {
    return {
        token: state.auth.apiToken,
        error: state.auth.error,
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<State, void, AnyAction>) {
    return {
        logout: bindActionCreators(ActionCreators.logout, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps, null)(VerifyLoginPage);
